import { useAtomValue } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { createRoot } from 'react-dom/client';
import { Utils } from '../../Common/Utils';
import {
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay2,
    LabelDisplay3,
} from '../modules/components';
import { exercisesAtom } from '../modules/data';
import { getExerciseData } from '../modules/exercises';
import {
    ExerciseDto,
    LogbookPersonalEntryDto,
    ModuleDto,
    Program,
    ThermometerDataPoint,
} from '../modules/interfaces';
import { hydratedThermometerDataPointsAtom } from '../thermometer/data';
import {
    exercisesWithAnswersAtom,
    initialProgramAtom,
    modulesAtom,
    modulesWithAvailableDataAtom,
} from './atoms';
import { ExerciseView } from './views/ExerciseView';
import { ThermometerGraph } from './views/ThermometerGraph';

type Props = {
    program: Program;
    dataPoints: ThermometerDataPoint[];
    exercises: ExerciseDto[];
    logbookPersonalEntries: LogbookPersonalEntryDto[];
    modules: ModuleDto[];
};

export function SetupAllDataApp(containerQuery: string, data: Props) {
    const elm = document.querySelector(containerQuery);
    if (elm == null) {
        throw new Error(
            `Could not find React root element with query ${containerQuery}`,
        );
    }
    const root = createRoot(elm);
    root.render(<AllDataApp {...data} />);
}

function AllDataApp({
    dataPoints,
    exercises,
    logbookPersonalEntries,
    modules,
    program,
}: Props) {
    useHydrateAtoms([
        [exercisesAtom, exercises],
        [modulesAtom, modules],
        [initialProgramAtom, program],
        [hydratedThermometerDataPointsAtom, dataPoints],
    ]);

    const exercisesWithAnswers = useAtomValue(exercisesWithAnswersAtom);
    const visibleModules = useAtomValue(modulesWithAvailableDataAtom);

    return (
        <div className="mx-20">
            <div className="my-20 text-right print:hidden">
                <GhostButton onClick={() => window.print()}>Print</GhostButton>
            </div>
            {visibleModules.map((module) => (
                <div key={module.Id} className="mt-20">
                    <LabelDisplay3>
                        {module.Name}: {module.Title}
                    </LabelDisplay3>
                    {exercisesWithAnswers
                        .filter((x) => x.exercise.ModuleId.Value === module.Id)
                        .map(({ exercise }) => (
                            <div key={exercise.Id} className="mt-20">
                                <LabelDisplay2>{exercise.Name}</LabelDisplay2>
                                <ExerciseView
                                    exercise={exercise}
                                    exerciseData={getExerciseData(
                                        program,
                                        exercise.Code,
                                    )}
                                />
                            </div>
                        ))}
                </div>
            ))}

            <LabelDisplay3>Dine temperaturtjek</LabelDisplay3>
            <ThermometerGraph />

            <LabelDisplay3>Egne noter</LabelDisplay3>
            {logbookPersonalEntries.map((entry) => (
                <div key={entry.Id} className="mb-20">
                    <LabelBodySmall>
                        {Utils.formatShortDate(
                            entry.UpdatedOn ?? entry.CreatedOn,
                        )}
                    </LabelBodySmall>
                    <LabelBodyHeadline color="text-dark-red">
                        {entry.Title}
                    </LabelBodyHeadline>
                    <LabelBody className="whitespace-pre">
                        {entry.Text}
                    </LabelBody>
                </div>
            ))}
        </div>
    );
}
